/**
 * RoadmapPage — /career-transition/roadmap  (latest)
 *                /career-transition/roadmap/:id (specific)
 *
 * Orchestrator for the Roadmap Viewer v1 surface (Phase 5d). Composes the
 * Stage A primitives, Stage B building blocks, and Stage C composite chrome
 * around two React Query data sources:
 *
 *   1. The roadmap itself (LLM-generated, immutable per generation).
 *   2. Per-task completion overlay (mutable, written via toggle endpoint).
 *
 * The merge happens client-side because backend's roadmap fetch always
 * returns task.done=false; truth lives in /tasks/completed (Phase 4b).
 *
 * Layout (per spec §10):
 *   AppRail (auto via routing) · RoadmapMinimap (220px sticky, hides <1100px)
 *   · main column (max-960px) containing RoadmapStickyHeader + RoadmapIntro
 *   + 4 PhaseSections (phases 1+2 full detail, phases 3+4 compact per §5.2).
 */

import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTranslation } from "react-i18next";

import {
  getLatestRoadmap,
  getRoadmapById,
  getRoadmapTaskCompletions,
  toggleRoadmapTask,
  RoadmapResponse,
  RoadmapTask,
  RoadmapTaskCompletion,
} from "../api/client";

import RoadmapIntro from "../components/roadmap/RoadmapIntro";
import RoadmapMinimap from "../components/roadmap/RoadmapMinimap";
import RoadmapStickyHeader from "../components/roadmap/RoadmapStickyHeader";
import PhaseSection from "../components/roadmap/PhaseSection";

const COMPACT_PHASE_THRESHOLD = 3; // phases ≥3 render compact per spec §5.2
const CONTENT_MAX_WIDTH = 960; // spec §2

export default function RoadmapPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const { t } = useTranslation("pages-account");

  // ── Data ───────────────────────────────────────────────────────────────
  const {
    data: roadmap,
    isLoading: roadmapLoading,
    error: roadmapError,
  } = useQuery<RoadmapResponse>({
    queryKey: ["roadmap", id ?? "latest"],
    queryFn: () => (id ? getRoadmapById(id) : getLatestRoadmap()),
    retry: false,
  });

  const roadmapId = roadmap?.id;
  const { data: completions = [] } = useQuery<RoadmapTaskCompletion[]>({
    queryKey: ["roadmap-task-completions", roadmapId],
    queryFn: () => getRoadmapTaskCompletions(roadmapId!),
    enabled: !!roadmapId,
  });

  // ── Merged state ───────────────────────────────────────────────────────
  // Two-pass walk over the phases/weeks/tasks tree:
  //   Pass 1: stamp `done` from the completions overlay.
  //   Pass 2: derive phase.status + week.status from completion state.
  //
  // The status derivation is necessary because the backend currently
  // defaults every phase + week to `status: "upcoming"` (server-side
  // progress tracking deferred to a future migration). Without derivation,
  // the entire roadmap looks "upcoming" forever — PhaseSection chips +
  // status dots + sticky header all show stale state.
  //
  // Rules: explicit server status wins. A phase/week with all tasks done
  // becomes "done." First non-done phase becomes "current"; everything
  // after is "upcoming." Same waterfall within the current phase's weeks.
  const merged = useMemo(() => {
    if (!roadmap) return null;
    const doneIds = new Set(completions.map((c) => c.task_id));

    // Pass 1: merge done state
    const withDone = roadmap.phases.map((p) => ({
      ...p,
      weeks: p.weeks.map((w) => ({
        ...w,
        tasks: (w.tasks ?? []).map((task) => ({ ...task, done: doneIds.has(task.id) })),
      })),
    }));

    // Pass 2: derive statuses
    let currentPhaseAssigned = false;
    const withStatus = withDone.map((p) => {
      const allTasks = p.weeks.flatMap((w) => w.tasks);
      const phaseAllDone = allTasks.length > 0 && allTasks.every((t) => t.done);

      let phaseStatus: "done" | "current" | "upcoming";
      if (p.status === "done" || phaseAllDone) {
        phaseStatus = "done";
      } else if (p.status === "current" || !currentPhaseAssigned) {
        phaseStatus = "current";
        currentPhaseAssigned = true;
      } else {
        phaseStatus = "upcoming";
      }

      // Derive week statuses within this phase. Only the current phase
      // gets a "current" week — future phases stay all-upcoming.
      let currentWeekAssigned = phaseStatus !== "current";
      const weeks = p.weeks.map((w) => {
        const weekAllDone = w.tasks.length > 0 && w.tasks.every((t) => t.done);
        let weekStatus: "done" | "current" | "upcoming";
        if (w.status === "done" || weekAllDone) {
          weekStatus = "done";
        } else if (w.status === "current" || !currentWeekAssigned) {
          weekStatus = "current";
          currentWeekAssigned = true;
        } else {
          weekStatus = "upcoming";
        }
        return { ...w, status: weekStatus };
      });

      return { ...p, status: phaseStatus, weeks };
    });

    return { ...roadmap, phases: withStatus };
  }, [roadmap, completions]);

  // ── Derived selectors ──────────────────────────────────────────────────
  const allTasks = useMemo(
    () =>
      merged
        ? merged.phases.flatMap((p) => p.weeks.flatMap((w) => w.tasks ?? []))
        : [],
    [merged],
  );

  const progress = useMemo(() => {
    const done = allTasks.filter((t) => t.done).length;
    const total = allTasks.length;
    return {
      done,
      total,
      pct: total > 0 ? Math.round((done / total) * 100) : 0,
    };
  }, [allTasks]);

  // currentPhase/currentWeek are now simple lookups against the derived
  // statuses set in the merged useMemo above. If no phase is "current" it
  // means everything's done — fall back to the last phase so the user
  // still sees something in the sticky header.
  const currentPhase =
    merged?.phases.find((p) => p.status === "current") ??
    merged?.phases[merged.phases.length - 1] ??
    null;
  const currentWeek =
    currentPhase?.weeks.find((w) => w.status === "current") ??
    currentPhase?.weeks[currentPhase.weeks.length - 1] ??
    null;

  // Focus task: prefer server-set `is_focus` when not already done.
  // Fallback: first unchecked + unblocked task in the current week.
  // Spec §4.3 — server hardcodes is_focus to phase1/week1/task1; once that
  // task is done, we want the title to advance, hence the fallback.
  const focusTask = useMemo<RoadmapTask | null>(() => {
    if (!merged) return null;
    for (const p of merged.phases) {
      for (const w of p.weeks) {
        for (const task of w.tasks ?? []) {
          if (task.is_focus && !task.done) return task;
        }
      }
    }
    if (currentWeek) {
      const next = currentWeek.tasks.find((task) => !task.done && !task.blocked);
      if (next) return next;
    }
    return null;
  }, [merged, currentWeek]);

  const nextMilestone = currentPhase
    ? {
        label: t("RoadmapPage.finishMilestoneLabel", { title: currentPhase.title }),
        weekIndex: currentPhase.week_range[1],
      }
    : null;

  // ── Mutation ───────────────────────────────────────────────────────────
  const toggleMutation = useMutation({
    mutationFn: ({ taskId, completed }: { taskId: string; completed: boolean }) =>
      toggleRoadmapTask(roadmapId!, taskId, completed),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["roadmap-task-completions", roadmapId],
      });
    },
  });

  const handleToggle = (taskId: string, next: boolean) => {
    if (!roadmapId) return;
    toggleMutation.mutate({ taskId, completed: next });
  };

  // ── Loading / error / empty ────────────────────────────────────────────
  if (roadmapLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 10, textAlign: "center" }}>
        <CircularProgress color="secondary" />
        <Typography variant="body2" color="text.secondary" mt={2}>
          {t("RoadmapPage.loadingRoadmap")}
        </Typography>
      </Container>
    );
  }

  if (roadmapError || !merged) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("RoadmapPage.noRoadmapMessage")}
        </Alert>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate("/career-transition/assessment")}
          startIcon={<AutoAwesomeIcon />}
        >
          {t("RoadmapPage.startAssessmentButton")}
        </Button>
      </Container>
    );
  }

  // Live announcement text for screen readers — updates when progress changes.
  // role="status" + aria-live="polite" means the announcement waits for the
  // user to finish their current input before reading. Visually hidden via
  // the standard sr-only CSS pattern (clip-path technique).
  const liveRegionMessage =
    progress.total > 0
      ? t("RoadmapPage.liveRegionMessage", { done: progress.done, total: progress.total, pct: progress.pct })
      : "";

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <RoadmapMinimap
        roadmap={merged}
        progress={progress}
        currentWeekIndex={currentWeek?.index ?? null}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <RoadmapStickyHeader
          currentTask={focusTask}
          currentPhase={currentPhase}
          currentWeek={currentWeek}
          nextMilestone={nextMilestone}
        />
        <Container
          sx={{
            maxWidth: `${CONTENT_MAX_WIDTH}px !important`,
            pt: { xs: 3, md: 5 },
            pb: { xs: 6, md: 10 },
          }}
        >
          <RoadmapIntro roadmap={merged} />
          <Stack spacing={6}>
            {merged.phases.map((phase) => (
              <PhaseSection
                key={phase.index}
                phase={phase}
                compact={phase.index >= COMPACT_PHASE_THRESHOLD}
                focusTaskId={focusTask?.id}
                onToggle={handleToggle}
              />
            ))}
          </Stack>
        </Container>
      </Box>
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {liveRegionMessage}
      </Box>
    </Box>
  );
}
